<template>
  <section class="sbc-activities">
    <div class="sbc-activities__inner">
      <div class="sbc-activities__head">
        <div>
          <div class="sbc-activities__kicker">{{ $t('event') }}</div>
          <h2 class="sbc-activities__title">{{ $t('activities_section_title') }}</h2>
        </div>
        <NuxtLink class="sbc-activities__cta" :to="redirect('events')" aria-label="See all events">
          {{ $t('view_all_events') }}
        </NuxtLink>
      </div>
      <p class="sbc-activities__desc">{{ $t('activities_section_desc') }}</p>

      <!-- Loading: shimmering skeleton cards, same footprint as the real ones -->
      <div v-if="loading" class="sbc-activities__skeleton" aria-hidden="true">
        <div class="sbc-activities__track sbc-activities__track--skeleton">
          <div v-for="n in 4" :key="n" class="sbc-activity sbc-activity--skeleton">
            <div class="sbc-activity__media sbc-skel"></div>
          </div>
        </div>
      </div>

      <div v-else class="sbc-activities__viewport">
        <div class="sbc-activities__track" ref="trackEl" @scroll="onTrackScroll" v-reveal>
          <article v-for="(item, index) in orderedActivities" :key="item.slug || index" class="sbc-activity" :style="{ '--i': index }">
            <NuxtLink :to="activityLink(item)" class="sbc-activity__card">
              <NuxtImg
                format="webp"
                class="sbc-activity__img"
                :src="img(item.cover)"
                :alt="item.title"
                loading="lazy"
              />
              <div class="sbc-activity__scrim" aria-hidden="true"></div>
              <div class="sbc-activity__body">
                <span class="sbc-activity__kicker">{{ tabLabel(item) }}</span>
                <h3 class="sbc-activity__name">{{ item.title }}</h3>
                <span class="sbc-activity__link">
                  {{ $t('detail') }}
                  <span class="sbc-activity__arrow" aria-hidden="true">&rarr;</span>
                </span>
              </div>
            </NuxtLink>
          </article>
        </div>

        <button
          v-if="!atStart"
          type="button"
          class="sbc-activities__arrow sbc-activities__arrow--prev"
          aria-label="Previous"
          @click="scrollByCard(-1)"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
        </button>
        <button
          v-if="!atEnd"
          type="button"
          class="sbc-activities__arrow sbc-activities__arrow--next"
          aria-label="Next"
          @click="scrollByCard(1)"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
        </button>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "EventsSection",
  data() {
    return {
      activities: [],
      loading: true,
      atStart: true,
      atEnd: false,
      isDesktop: false,
    };
  },
  computed: {
    // Desktop shows the event calendar first; mobile keeps it last so it
    // fills the full-width bottom row of the 2 + 2 + 1 grid.
    orderedActivities() {
      if (!this.isDesktop) return this.activities;
      const isCalendar = (item) => String(item.slug).includes('event-calendar');
      return [
        ...this.activities.filter(isCalendar),
        ...this.activities.filter((item) => !isCalendar(item)),
      ];
    },
  },
  setup() {
    const { locale, setLocale } = useI18n();

    const language = computed({
      get: () => locale.value,
      set: (value) => {
        setLocale(value);
      },
    });

    return { language };
  },
  methods: {
    async getData() {
      const TAB_MAP = {
        '?tab=concerts': '#concerts',
        '?tab=animations': '#animations',
        '?tab=meetings': '#meetings',
        '?tab=organizations': '#weddings',
      };
      await $fetch(
        `https://api2.salamisbayconti.com/api/general/${this.language}/activity`,
        {
          method: "GET",
        }
      )
        .then((res) => {
          this.activities = res.map(item => ({
            ...item,
            slug: TAB_MAP[item.slug] ?? item.slug,
          }));
          this.loading = false;
          this.$nextTick(this.onTrackScroll);
        })
        .catch((e) => {
          this.loading = false;
          console.log(e);
        });
    },
    img(photo) {
      return `https://api2.salamisbayconti.com/storage/${photo}`;
    },
    // Cards echo the destination tab's own label instead of a generic
    // "activity" word, so the category is legible before the image loads.
    tabLabel(item) {
      const map = {
        '#concerts': this.$t('concerts'),
        '#animations': this.$t('animations'),
        '#meetings': this.$t('meeting_saloon'),
        '#weddings': this.$t('wedding_saloon'),
      };
      return map[item.slug] || this.$t('event_calendar');
    },
    activityLink(item) {
      const base = this.redirect('events');
      if (item.slug && item.slug.startsWith('#')) return `${base}${item.slug}`;
      return item.slug ? `${base}/${item.slug}` : base;
    },
    redirect(route) {
      if (this.language === 'en') {
        return `/en/${route}`;
      }
      return `/${route}`;
    },
    onDesktopChange(e) {
      this.isDesktop = e.matches;
    },
    onTrackScroll() {
      const el = this.$refs.trackEl;
      if (!el) return;
      this.atStart = el.scrollLeft <= 4;
      this.atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    },
    scrollByCard(dir) {
      const el = this.$refs.trackEl;
      if (!el) return;
      const card = el.querySelector(".sbc-activity");
      const cardWidth = card ? card.getBoundingClientRect().width : 340;
      el.scrollBy({ left: dir * (cardWidth + 22), behavior: "smooth" });
    },
  },
  mounted() {
    this.desktopMq = window.matchMedia("(min-width: 768px)");
    this.isDesktop = this.desktopMq.matches;
    this.desktopMq.addEventListener("change", this.onDesktopChange);
    this.getData();
    window.addEventListener("resize", this.onTrackScroll);
  },
  beforeUnmount() {
    this.desktopMq?.removeEventListener("change", this.onDesktopChange);
    window.removeEventListener("resize", this.onTrackScroll);
  },
};
</script>

<style scoped>
.sbc-activities {
  --sbc-navy-900: #0B1F2E;
  --sbc-gold-400: #D9B96B;
  --sbc-gold-600: #C19A45;
  background: #FBF9F6;
  padding: clamp(56px, 6vw, 100px) clamp(20px, 4vw, 48px);
}
.sbc-activities__inner { max-width: 1320px; margin: 0 auto; }

.sbc-activities__head {
  display: flex; align-items: flex-end; justify-content: space-between;
  gap: 32px; flex-wrap: wrap;
}
.sbc-activities__kicker {
  font-family: "Cormorant Garamond", Georgia, serif; font-size: .8125rem;
  letter-spacing: .28em; text-transform: uppercase; color: var(--sbc-navy-900);
  font-weight: 700; text-shadow: 0 2px 6px rgba(11,31,46,.35);
}
.sbc-activities__title {
  font-family: "Cormorant Garamond", Georgia, serif; font-weight: 300;
  font-size: clamp(2rem, 3.6vw, 3.25rem); line-height: 1.15;
  color: var(--sbc-navy-900); margin: 16px 0 0;
}
.sbc-activities__desc {
  font-family: "Jost", sans-serif; font-size: clamp(17px, 1.35vw, 21px);
  font-weight: 300; line-height: 1.75; color: rgba(11,31,46,.72);
  max-width: 720px; margin: 20px 0 clamp(36px, 4vw, 56px);
}
.sbc-activities__cta {
  flex-shrink: 0;
  font-size: .8125rem; font-weight: 700; letter-spacing: .16em; text-transform: uppercase;
  color: var(--sbc-navy-900); text-decoration: none;
  border-bottom: 1px solid var(--sbc-gold-600); padding-bottom: 3px;
  transition: color 200ms ease, border-color 200ms ease;
}
.sbc-activities__cta:hover { color: var(--sbc-gold-600); border-color: var(--sbc-navy-900); }

.sbc-activities__viewport { position: relative; }
.sbc-activities__track {
  display: flex;
  flex-wrap: nowrap;
  gap: 22px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.sbc-activities__track::-webkit-scrollbar { display: none; }

.sbc-activities__arrow {
  position: absolute; top: 220px;
  width: 44px; height: 44px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: rgba(11,31,46,.06); border: 1px solid rgba(11,31,46,.24);
  backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
  color: var(--sbc-navy-900); cursor: pointer; z-index: 3;
  transition: background 200ms ease, border-color 200ms ease, color 200ms ease;
}
.sbc-activities__arrow:hover {
  background: var(--sbc-gold-400); border-color: var(--sbc-gold-400); color: var(--sbc-navy-900);
}
.sbc-activities__arrow--prev { left: -8px; }
.sbc-activities__arrow--next { right: -8px; }

.sbc-activity {
  flex: 0 0 auto; width: 340px;
  scroll-snap-align: start;
}
.sbc-activity__card {
  position: relative; display: block;
  height: 440px; border-radius: 4px; overflow: hidden;
  background: var(--sbc-navy-900); text-decoration: none;
}
.sbc-activity__img {
  width: 100%; height: 100%; object-fit: cover; display: block;
  transition: transform 520ms cubic-bezier(.22,.61,.36,1);
}
.sbc-activity__card:hover .sbc-activity__img { transform: scale(1.06); }
/* Card images zoom in one after another. Uses the standalone `scale`
   property so it composes with the hover `transform` above. */
.sbc-activities__track[data-reveal="in"] .sbc-activity__img {
  transition:
    transform 520ms cubic-bezier(.22,.61,.36,1),
    scale 2200ms cubic-bezier(.2, .7, .2, 1),
    opacity 1400ms ease;
  transition-delay: 0s, calc(min(var(--i, 0), 8) * 320ms), calc(min(var(--i, 0), 8) * 320ms);
}
.sbc-activities__track[data-reveal="out"] .sbc-activity__img {
  scale: 1.3;
  opacity: 0;
}
.sbc-activity__scrim {
  position: absolute; inset: 0;
  background: linear-gradient(to top, rgba(11,31,46,.92) 0%, rgba(11,31,46,.2) 55%, rgba(11,31,46,0) 78%);
}
.sbc-activity__body {
  position: absolute; left: 0; right: 0; bottom: 0;
  display: flex; flex-direction: column; gap: 8px;
  padding: 26px 24px;
}
.sbc-activity__kicker {
  font-family: "Jost", sans-serif; font-size: .75rem; font-weight: 500;
  letter-spacing: .2em; text-transform: uppercase; color: var(--sbc-gold-400);
  text-shadow: 0 2px 6px rgba(11,31,46,.35);
}
.sbc-activity__name {
  font-family: "Cormorant Garamond", Georgia, serif; font-weight: 300;
  font-size: 1.85rem; line-height: 1.15; color: #FBF9F6; margin: 0;
}
.sbc-activity__link {
  display: inline-flex; align-items: center; gap: 8px; margin-top: 4px;
  font-family: "Jost", sans-serif; font-size: .75rem; font-weight: 500;
  letter-spacing: .14em; text-transform: uppercase; color: #FBF9F6;
  opacity: 0; transform: translateY(6px);
  transition: opacity 250ms ease, transform 250ms ease;
}
.sbc-activity__card:hover .sbc-activity__link { opacity: 1; transform: translateY(0); }
.sbc-activity__arrow { font-size: .875rem; line-height: 1; transition: transform 200ms ease; }
.sbc-activity__card:hover .sbc-activity__arrow { transform: translateX(3px); }

/* Skeleton loading state — mirrors real card sizing so layout doesn't shift */
.sbc-activities__skeleton { position: relative; }
.sbc-activities__track--skeleton { overflow: hidden; }
.sbc-activity--skeleton { cursor: default; }
.sbc-activity__media.sbc-skel { height: 440px; border-radius: 4px; }
.sbc-skel {
  position: relative;
  overflow: hidden;
  background: rgba(11,31,46,.08);
}
.sbc-skel::after {
  content: "";
  position: absolute; inset: 0;
  background: linear-gradient(90deg, transparent, rgba(11,31,46,.10), transparent);
  transform: translateX(-100%);
  animation: sbc-activities-skel-shimmer 1.5s ease-in-out infinite;
}
@keyframes sbc-activities-skel-shimmer {
  100% { transform: translateX(100%); }
}

@media (prefers-reduced-motion: reduce) {
  .sbc-skel::after { animation: none; }
}

@media (max-width: 767px) {
  .sbc-activities__head { align-items: flex-start; }
  .sbc-activities__kicker {
    font-size: .6875rem;
    letter-spacing: .2em;
  }
  .sbc-activities__title {
    font-size: clamp(2.25rem, 11vw, 3.25rem);
    line-height: 1.05;
    margin-top: 12px;
  }
  .sbc-activities__desc {
    font-size: 1.0625rem;
    line-height: 1.55;
    margin-top: 16px;
  }
  .sbc-activities__cta {
    margin-top: 4px;
    font-size: .6875rem;
    letter-spacing: .14em;
  }
  /* Mobile: 2 + 2 + 1 grid instead of the swipe track. A last card left
     alone in its row (the event calendar) spans the full width. */
  .sbc-activities__track {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    overflow: visible;
    scroll-snap-type: none;
  }
  .sbc-activity { width: auto; }
  .sbc-activity:last-child:nth-child(odd) { grid-column: 1 / -1; }
  .sbc-activity__card,
  .sbc-activity__media.sbc-skel { height: 210px; }
  .sbc-activity:last-child:nth-child(odd) .sbc-activity__card { height: 170px; }
  .sbc-activities__arrow { display: none; }
  .sbc-activity__body { gap: 4px; padding: 14px 12px; }
  .sbc-activity__kicker { font-size: .5625rem; letter-spacing: .14em; }
  .sbc-activity__name { font-size: 1.2rem; line-height: 1.15; }
  .sbc-activity__link { font-size: .5625rem; letter-spacing: .1em; gap: 5px; margin-top: 2px; }
  /* No hover on touch devices — keep the destination link visible always. */
  .sbc-activity__link { opacity: 1; transform: none; }
}
/* Mobile: +10px vertical padding */
@media (max-width: 767px) {
  .sbc-activities { padding-block: calc(clamp(56px, 6vw, 100px) + 10px); }
}
</style>
